import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import { RequireAuth } from "../shared/RequireAuth";
import { useAuth } from '../shared/contexts/AuthContext';

// -----------------------------------------------
//Admin
// -----------------------------------------------
import { AdminAuthProvider } from './admin/AdminAuthContext';
import { AdminGuard } from './admin/AdminGuard';
import LoginAdmin from './admin/LoginAdmin';
import AdminHome from './admin/AdminHome';
import MusicAdmin from './admin/MusicAdmin';
import AdminAdsReview from './admin/AdminAdsReview';
import AdminBillingOverview from './admin/AdminBillingOverview';
import AdminMediaModeration from './admin/AdminMediaModeration';
import AdminPostReport from "./admin/AdminPostReport";
// -----------------------------------------------
// Legal
// -----------------------------------------------
import TermsOfUse from '../shared/legal/TermsOfUse'; 
import PrivacyPolicy from '../shared/legal/PrivacyPolicyWeb'; 


// -----------------------------------------------
// Administration
// -----------------------------------------------
import StartPage from './pages/Administration/StartPage';
import ProfileSetup from './pages/Administration/ProfileSetup';
import ForgotPassword from './pages/Administration/ForgotPassword';
import ResetPassword from './pages/Administration/ResetPassword';
import ConfirmEmail from '../shared/Administration/ConfirmEmail';
import EmailSent from './pages/Administration/EmailSent';
import CheckEmail from './pages/Administration/CheckEmail';
import FirstRunPrivacy from './pages/Administration/FirstRunPrivacy';
import RequirePrivacyChange from './pages/Administration/RequirePrivacyChange';
import RequirePrivacyChangeCL from './pages/Administration/RequirePrivacyChangeCL';
import RequirePrivacyChangeMap from './pages/Administration/RequirePrivacyChangeMap';
import BlockedList from "./pages/BlockedList";
import ProfileManagement from "./settings/ProfileManagement";    


// -----------------------------------------------
//Help & Support
// -----------------------------------------------
import HelpSupportStart from "./pages/HelpSupport/HelpSupportStart";
import PlatformGuide from "./pages/HelpSupport/PlatformGuide";
import AccountAdministration from "./pages/HelpSupport/AccountAdministration";
import Faq from "./pages/HelpSupport/Faq";
import AdvertisementGuide from "./pages/HelpSupport/AdvertisementGuide";
import ContactSupport from "./pages/HelpSupport/ContactSupport";


// -----------------------------------------------
// Settings
// -----------------------------------------------
import StartSetting from './settings/StartSetting';
import AccountSettings from './settings/AccountSettings';
import DataPrivacySettings from './settings/DataPrivacySettings';
import SecuritySettings from './settings/SecuritySettings';


// -----------------------------------------------
// Pages/Menu
// -----------------------------------------------
//Media Feeds
import GeneralBlog from './pages/Gallery/Gallery';
import FollowingFeed from './pages/MyProfile/FollowingFeed';
import MyLikes from './pages/MyProfile/MyLikes';
import VisitorPostings from './pages/MyProfile/VisitorPostings';
import MusicDetailPosts from './pages/Presentation/MusicDetailPosts';
import MusicDetailPage from './pages/Presentation/MusicDetailPage';

//Profile
import Profile from './pages/MyProfile/Profile';
import MyPostings from './pages/MyProfile/MyPostings';
import FollowingList from './pages/MyProfile/FollowingList';
import FollowersList from './pages/MyProfile/FollowersList';

//Location
import ChangeLocationGeo from './pages/LocationHandling/ChangeLocationGeo';
import ChangeLocationNogeo from './pages/LocationHandling/ChangeLocationNogeo';
import ConfirmLocation from './pages/LocationHandling/confirm-location';
import { useLocationHandling } from './pages/LocationHandling/useLocationHandling';
import LocationCollection from './pages/Presentation/LocationCollection';
import LocationPostDetail from './pages/Presentation/LocationPostDetail';

//Presentation
import MediaPresentationMode from './pages/Presentation/MediaPresentationMode';

//Notifications
import Notifications from './pages/MyProfile/Notifications';   


// -----------------------------------------------
//Gallery posting pages
// -----------------------------------------------
import GalleryPosting from './pages/Gallery/GalleryPosting';
import GalleryPostEditing from './pages/MyProfile/GalleryPostEditing'; 
import GalleryEditPosting from './pages/Gallery/GalleryEditPosting';
import ImageFilter from './pages/Gallery/ImageFilter';
import VideoFilter from './pages/Gallery/VideoFilter';
import AddMusic from './pages/Gallery/AddMusic';
import ChooseTrack from './pages/Gallery/ChooseTrack';

//Components
import EditMedia from './components/EditMedia';

// -----------------------------------------------
// Chat
// -----------------------------------------------
import Chat from './pages/Chat/Chat';
import ChatMediaPresentation from './pages/Chat/ChatMediaPresentation';
import ChatUploadMedia from './pages/Chat/ChatUploadMedia';

// -----------------------------------------------
//Advertisement
// -----------------------------------------------

import AdHome from "./Advertisement/AdHome";
import ProfileSetupAds from './Advertisement/Admin/ProfileSetupAds';
import ForgotPasswordAds from './Advertisement/Admin/ForgotPasswordAds';
import EmailSentAds from './Advertisement/Admin/EmailSentAds';
import ConfirmEmailAds from "./Advertisement/Admin/ConfirmEmailAds";
import ResetPasswordAds from "./Advertisement/Admin/ResetPasswordAds";
import AdLogin from "./Advertisement/Admin/AdLogin";
import AdRegistration from "./Advertisement/Admin/AdRegistration";
import Billing from "./Advertisement/Admin/Billing";

import AdvertisementStart from "./Advertisement/AdvertisementStart";
import AdUpload from "./Advertisement/AdUpload";
import AdOverview from "./Advertisement/AdOverview";
import ViewAdDetail from "./Advertisement/ViewAdDetail";
import ManageBillings from "./Advertisement/ManageBillings";
import ChangePayment from "./Advertisement/ChangePayment";
import PostUploadAd from "./Advertisement/PostUploadAd";

//Settings
import StartSettingAd from './Advertisement/settings/StartSettingAd';
import AccountSettingsAd from './Advertisement/settings/AccountSettingsAd';
import DataPrivacySettingsAd from './Advertisement/settings/DataPrivacySettingsAd';
import SecuritySettingsAd from './Advertisement/settings/SecuritySettingsAd';
import ProfileManagementAd from "./Advertisement/settings/ProfileManagementAd";    

//Ad Media
import AdImageFilter from "./Advertisement/MediaEdit/AdImageFilter";
import AdVideoFilter from "./Advertisement/MediaEdit/AdVideoFilter";
import AdAddMusic from "./Advertisement/MediaEdit/AdAddMusic";
import AdChooseTrack from "./Advertisement/MediaEdit/AdChooseTrack";

// -----------------------------------------------
//Routes AppWrapper
// -----------------------------------------------
function AppWrapper() {
  const { isAuthenticated, user, token, isBootstrapping } = useAuth();
  const { initializeLocation } = useLocationHandling();

  React.useEffect(() => {
    if (isAuthenticated && user && token) {
      initializeLocation();
    }
  }, [isAuthenticated, user, token]);

  if (isBootstrapping) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        Loading...
      </div>
    );
  }
return (
  <div style={{ backgroundColor: 'var(--bg)', color: 'var(--text)', minHeight: '100vh', width: '100%', overflowX: 'hidden' }}>

<Routes>

{/* Admin */}
<Route path="/admin/login" element={<LoginAdmin />} />

<Route
  path="/admin"
  element={
    <AdminAuthProvider>
      <AdminGuard>
        <AdminHome />
      </AdminGuard>
    </AdminAuthProvider>
  }
/>

<Route
  path="/admin/billing"
  element={
    <AdminAuthProvider>
      <AdminGuard>
        <AdminBillingOverview />
      </AdminGuard>
    </AdminAuthProvider>
  }
/>

<Route
  path="/admin/ads-review"
  element={
    <AdminAuthProvider>
      <AdminGuard>
        <AdminAdsReview />
      </AdminGuard>
    </AdminAuthProvider>
  }
/>

<Route
  path="/admin/media"
  element={
    <AdminAuthProvider>
      <AdminGuard>
        <AdminMediaModeration />
      </AdminGuard>
    </AdminAuthProvider>
  }
/>

<Route
  path="/admin/post-reports"
  element={
    <AdminAuthProvider>
      <AdminGuard>
        <AdminPostReport />
      </AdminGuard>
    </AdminAuthProvider>
  }
/>


<Route
  path="/upload-music"
  element={
    <AdminAuthProvider>
      <AdminGuard>
        <MusicAdmin />
      </AdminGuard>
    </AdminAuthProvider>
  }
/>


{/* Public Routes */}
  {/* Homepage */}
  <Route path="/" element={<StartPage />} />

  {/* Ad Homepage */}
  <Route path="/ads-home" element={<AdHome />}/>


  {/* Administration */}
  <Route path="/start" element={<Navigate to="/" replace />} />
  <Route path="/profile-setup" element={<ProfileSetup />} />
  <Route path="/reset-password/:token" element={<ResetPassword />} />
  <Route path="/confirm-email" element={<ConfirmEmail />} />
  <Route path="/forgot-password" element={<ForgotPassword />} />
  <Route path="/email-sent" element={<EmailSent />} />
  <Route path="/check-email" element={<CheckEmail />} />

  {/* Legal */}
  <Route path="/legal/termsofuse" element={<TermsOfUse />} />
  <Route path="/legal/privacypolicy" element={<PrivacyPolicy />} />

  {/* Help&Support */}
  <Route path="/helpsupportstart" element={<HelpSupportStart />} />
  <Route path="/help/platform" element={<PlatformGuide />} />
  <Route path="/help/account" element={<AccountAdministration />} />
  <Route path="/help/faq" element={<Faq />} />
  <Route path="/help/advertisement" element={<AdvertisementGuide />} />
  <Route path="/help/contact" element={<ContactSupport />} />      

{/* ADVERTISEMENT */}
  <Route path="/ad-login" element={<AdLogin />} />      
  <Route path="/register" element={<ProfileSetupAds />} />      
  <Route path="/ad-forgot-password" element={<ForgotPasswordAds />} />      
  <Route path="/email-sent-ads" element={<EmailSentAds />} />      
  <Route path="/ad-reset-password/:token" element={<ResetPasswordAds />} />      
  <Route path="/confirm-email-ads" element={<ConfirmEmailAds />} />      

                      
{/* Protected Routes */}
<Route element={<RequireAuth />}>

  {/* Administration */}
  <Route path="/firstRun" element={<FirstRunPrivacy />} />
  <Route path="/require-privacy-change" element={<RequirePrivacyChange />} />
  <Route path="/require-privacy-changeCL" element={<RequirePrivacyChangeCL />} />
  <Route path="/require-privacy-changeMap" element={<RequirePrivacyChangeMap />} />

  {/* Settings */}
  <Route path="/settings/overview" element={<StartSetting />} />
  <Route path="/settings/account" element={<AccountSettings />} />
  <Route path="/settings/privacy" element={<DataPrivacySettings />} />
  <Route path="/settings/security" element={<SecuritySettings />} />
  <Route path="/settings/profile-management" element={<ProfileManagement />} />

  
  {/* Pages/Menu/Feeds/Presentation */}
  <Route path="/gallery" element={<GeneralBlog />} />
  <Route path="/following" element={<FollowingFeed />} />
  <Route path="/mylikes" element={<MyLikes />} />
  <Route path="/visitor-profile/:userId" element={<VisitorPostings />} />
  <Route path="/musicdetails/:id" element={<MusicDetailPage />} />
  <Route path="/musicdetails/:trackId/post/:postId" element={<MusicDetailPosts />} />

  {/* Posting */}
  <Route path="/gallery-posting" element={<GalleryPosting />} />
  <Route path="/edit-media" element={<EditMedia />} />
  <Route path="/posting/edit/:postId" element={<GalleryEditPosting />} />
  <Route path="/add-music" element={<AddMusic />} />
  <Route path="/choose-track" element={<ChooseTrack />} />

  {/* Presentation */}
  <Route path="/presentation/:userId/media" element={<MediaPresentationMode />} />

  {/* Profile */}
  <Route path="/profile" element={<Profile />} />
  <Route path="/my-posts" element={<MyPostings />} />
  <Route path="/blocked" element={<BlockedList />} />
  <Route path="/following-list" element={<FollowingList />} />
  <Route path="/followers-list" element={<FollowersList />} />

  {/* Notification */}
  <Route path="/notifications" element={<Notifications />} />

  {/* Location */}
  <Route path="/change-location-geo" element={<ChangeLocationGeo />} />
  <Route path="/change-location-nogeo" element={<ChangeLocationNogeo />} />
  <Route path="/confirm-location" element={<ConfirmLocation />} />
  <Route path="/location-collection" element={<LocationCollection />} />
  <Route path="/location-post-detail/:postId" element={<LocationPostDetail />} />


  {/* Editor */}
  <Route path="/edit/gallery/:id" element={<GalleryPostEditing />} />
  <Route path="/editor/image/:id" element={<ImageFilter />} />
  <Route path="/editor/video/:id" element={<VideoFilter />} />

  {/* Chat */}
  <Route path="/chat" element={<Chat />} />
  <Route path="/chat/:userId" element={<Chat />} />
  <Route path="/chat-upload-media" element={<ChatUploadMedia />} />
  <Route path="/chat/:userId/media" element={<ChatMediaPresentation />} />


  {/* Advertisement */}

    <Route path="/adregistration" element={<AdRegistration />} />      
    <Route path="/billing" element={<Billing />} />      

  <Route path="/advertisementstart" element={<AdvertisementStart />}/>
  <Route path="/adupload" element={<AdUpload />}/>
  <Route path="/adoverview" element={<AdOverview />}/>
  <Route path="/advertisement/campaign/:id" element={<ViewAdDetail />} />
  <Route path="/managebillings" element={<ManageBillings />}/>
  <Route path="/changepayment" element={<ChangePayment />}/>
  <Route path="/adeditor/image/:id" element={<AdImageFilter />} />
  <Route path="/adeditor/video/:id" element={<AdVideoFilter />} />
  <Route path="/ad-add-music" element={<AdAddMusic />} />
  <Route path="/ad-choose-track" element={<AdChooseTrack />} />
  <Route path="/advertisement/post-upload" element={<PostUploadAd />}/>
  
  <Route path="/ad/settings/overview" element={<StartSettingAd />} />
  <Route path="/ad/settings/account" element={<AccountSettingsAd />} />
  <Route path="/ad/settings/privacy" element={<DataPrivacySettingsAd />} />
  <Route path="/ad/settings/security" element={<SecuritySettingsAd />} />
  <Route path="/ad/settings/profile-management" element={<ProfileManagementAd />} />

  {/* Protected fallback */}
  <Route path="*" element={<Navigate to="/gallery" />} />
</Route>

  {/* Public fallback */}
  <Route path="*" element={<Navigate to="/" />} />
     
      </Routes>
    </div>
  );
}


function App() {
  return (
    <Router>
      <AppWrapper />
    </Router>
  );
}

export default App;
